import type { components } from "@libertify/api-types/media";
import { Slide } from "pages/media/types/";
import { mapSlideToTemplateState } from "../storyboardEditor/helpers/mapTemplateSlidesToTemplateState";
import { SlideLayout, TemplateState } from "../storyboardEditor/types";
import placeholders from "./placeholders";
import { ITEM_ID_TO_INPUT_KEY } from "./helpers";
import type { ThemeResponse } from "./hooks/useThemeApi";

type SlideTypeKey = components["schemas"]["SlideType"];
type CommonAssets = Parameters<typeof mapSlideToTemplateState>[3];

// Slide types previewed in the gallery, ordered to lead with the most
// representative layouts. Covers every slide type the editor renders.
export const GALLERY_SLIDE_TYPES: SlideTypeKey[] = [
  SlideLayout.TITLE,
  SlideLayout.BULLET_POINT,
  SlideLayout.IMAGE,
  SlideLayout.FIGURE,
  SlideLayout.PARAGRAPH_IMAGE,
  SlideLayout.QUOTE,
  SlideLayout.DOUBLE_PARAGRAPH,
];

/**
 * Build the editor template state for a single slide type of a theme.
 *
 * Shared by the editor (loading the selected theme into the store) and the
 * theme gallery (rendering read-only previews), so a gallery card is byte
 * identical to what the editor shows once the theme is opened. When the theme
 * has no saved layout for a slide type, the default layout items are used —
 * mirroring how the editor populates every slide type.
 */
export const buildThemeSlideTemplateState = (
  theme: ThemeResponse,
  slideType: SlideTypeKey,
  slidesPlaceholders: Slide[],
  mediaAspectRatio: number,
  commonAssets: CommonAssets
): TemplateState => {
  const savedLayout = theme.layouts[slideType];
  const defaultItems = placeholders.DEFAULT_SLIDES_ITEMS[slideType];
  const savedItems = savedLayout?.layout ?? defaultItems;

  const savedNames = new Set(
    savedItems
      .map((item) => item.name)
      .filter((name): name is string => !!name)
  );

  // Togglable items missing from the saved layout start hidden, matching the
  // editor's handling of optional elements (logo, avatar, …).
  const missingTogglableItems = defaultItems.filter(
    (item) =>
      !!item.name &&
      item.name in ITEM_ID_TO_INPUT_KEY &&
      !savedNames.has(item.name)
  );
  const disabledNames = new Set<string>(
    missingTogglableItems
      .map((item) => item.name)
      .filter((name): name is string => !!name)
  );

  return mapSlideToTemplateState(
    {
      id: "",
      // Preserve the per-slide avatar shot persisted with the layout so the
      // preview matches what the editor renders once the theme is opened.
      avatarShot: savedLayout?.avatar_shot ?? null,
      music: null,
      inputs: {},
      sprite_definitions: [...savedItems, ...missingTogglableItems],
      name: slideType,
    },
    slidesPlaceholders,
    mediaAspectRatio,
    commonAssets,
    disabledNames
  );
};
